import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Search, 
  Sparkles, 
  TrendingUp, 
  TrendingDown, 
  Grid, 
  List, 
  ChevronDown, 
  ArrowUp,
  Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import PostCard from '@/components/community/PostCard';
import PostForm from '@/components/community/PostForm';
import TrendingSidebar from '@/components/community/TrendingSidebar';
import { getPosts, Post } from '@/db/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const CommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await getPosts();
      setPosts(data);
    } catch (err) {
      toast.error('加载失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handlePostSuccess = (newPost: Post) => {
    setPosts(prev => [newPost, ...prev]);
  };

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      {/* 英雄区 */}
      <div className="tech-flow-bg h-[200px] md:h-[260px] flex flex-col justify-center px-6 md:px-12 relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <Badge className="bg-white/20 text-white backdrop-blur-md border-none mb-4 font-bold">
            创作者社区
          </Badge>
          <h1 className="text-desktop-h1 max-sm:text-mobile-h1 text-white mb-2 logo-glow">
            交流、分享、共创
          </h1>
          <p className="text-white/70 max-w-2xl mb-8">
            在这里发现最新的自媒体趋势，向行业顶尖创作者学习。每一个灵感，都能聚光。
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 -mt-10 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 左侧主内容区 */}
          <div className="lg:col-span-3 space-y-6">
            {/* 快捷搜索与筛选 */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 rounded-xl glass-card border-border/50 sticky top-20 z-10">
              <div className="relative w-full md:w-96">
                <Input 
                  placeholder="搜索动态、经验、教程..." 
                  className="rounded-lg border-primary/20 bg-background/50 pl-10 h-10"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex items-center gap-4 w-full md:w-auto">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-10 gap-2 border-border font-bold text-muted-foreground hover:text-primary"
                  onClick={() => {}}
                >
                  <Filter className="h-4 w-4" />
                  最新发布
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <div className="flex items-center bg-muted/50 p-1 rounded-lg border border-border">
                  <Button 
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
                    size="icon" 
                    className="h-8 w-8 rounded-md"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                    size="icon" 
                    className="h-8 w-8 rounded-md"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* 发帖组件 */}
            {user && <PostForm onSuccess={handlePostSuccess} />}

            {/* 帖子列表 */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-pulse">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-80 bg-muted rounded-xl"></div>
                ))}
              </div>
            ) : (
              <div className={
                viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 gap-6" 
                  : "flex flex-col gap-6"
              }>
                {posts.length > 0 ? (
                  posts.map(post => (
                    <PostCard key={post.id} post={post} />
                  ))
                ) : (
                  <Card className="rounded-xl border-dashed border-2 py-20 text-center col-span-full">
                    <CardContent>
                      <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
                      <h3 className="text-lg font-bold text-muted-foreground">暂无动态</h3>
                      <p className="text-sm text-muted-foreground/60">来发布第一篇动态吧！</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* 加载更多 */}
            {!loading && posts.length > 0 && (
              <Button 
                variant="outline" 
                className="w-full mt-10 border-border text-muted-foreground h-12 font-bold hover:bg-muted transition-all duration-300"
                onClick={() => {}}
              >
                查看更多动态
              </Button>
            )}
          </div>

          {/* 右侧边栏 */}
          <div className="space-y-8">
            <TrendingSidebar />
            
            <Card className="rounded-xl overflow-hidden glass-card border-border/50 p-6 space-y-4">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-bold">
                <Sparkles className="h-5 w-5" />
                公告
              </CardTitle>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                  <h5 className="text-xs font-bold text-primary mb-1">系统公告</h5>
                  <p className="text-xs text-muted-foreground leading-relaxed font-medium">万象聚聚光 3.0 版本正式上线！</p>
                </div>
                <div className="p-3 rounded-lg bg-secondary/5 border border-secondary/10">
                  <h5 className="text-xs font-bold text-secondary mb-1">创作者激励</h5>
                  <p className="text-xs text-muted-foreground leading-relaxed font-medium">分享经验，赢取万象内测会员特权！</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4 border-primary/20 text-primary h-10 font-bold rounded-lg shadow-sm"
                onClick={() => {}}
              >
                查看全部公告
              </Button>
            </Card>
          </div>
        </div>
      </div>

      {/* 回到顶部悬浮按钮 */}
      <Button 
        variant="secondary" 
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-float hover:scale-110 transition-transform p-0 flex items-center justify-center bg-background border border-border"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <ArrowUp className="h-6 w-6 text-primary" />
      </Button>
    </div>
  );
};

export default CommunityPage;
已